/**
 * Helpers for the document-generation provenance gate (demand letter / sworn PoL).
 *
 * The backend's gate returns a 412 whose `detail` is an OBJECT:
 *   { message: string, blocking_placeholders: string[], ... }
 * (see `_apply_provenance_gate` in backend/app/api/v1/attorney_tools.py). Callers that
 * naively did `new Error(err.detail)` stringified that object to "[object Object]".
 * `parseGenerationBlock` normalizes both the structured 412 and plain-string errors.
 */

/**
 * Attorney-OWNED letterhead placeholders — the ones an attorney can fix themselves at
 * /attorney/letterhead. Firm-owned placeholders ([FIRM NAME] / [FIRM ADDRESS] / [PHONE])
 * are set by an admin under Settings → Law Firms, NOT here, so they are deliberately excluded.
 */
export const LETTERHEAD_PLACEHOLDERS = ['[ATTORNEY NAME]', '[EMAIL]', '[BAR NUMBER]'];

export interface GenerationBlock {
  message: string;
  blockingPlaceholders: string[];
  /** 'not_attested' when the 412 is the attestation gate; otherwise 'generic'. */
  kind: 'not_attested' | 'generic';
  /** True for every attestation block: an accountant must (re-)attest before export. */
  reattestRequired: boolean;
  /** True when the case WAS attested/finalized and an edit invalidated it. */
  invalidated: boolean;
  electedMethod?: string;
  reviewStatus?: string;
}

/** Copy for the attestation-blocked state (court-ready bundle + attorney exports). */
export const NOT_ATTESTED_TITLE = 'Court-ready export blocked';
export const NOT_ATTESTED_BODY =
  "This case isn't attested. An accountant must attest the sworn figure before it can be exported.";
export const REATTEST_BODY = 'Re-attestation required — the case was edited after attestation.';

/** Body text for an attestation block (re-attest wording when an edit invalidated it). */
export function attestationBlockBody(block: Pick<GenerationBlock, 'invalidated'>): string {
  return block.invalidated ? REATTEST_BODY : NOT_ATTESTED_BODY;
}

/** Single-line message for pages that show errors as one string. */
export function attestationBlockLine(block: Pick<GenerationBlock, 'invalidated'>): string {
  return `${NOT_ATTESTED_TITLE} — ${attestationBlockBody(block)}`;
}

/**
 * Normalize a parsed generate/export error body into a display message + the list of
 * blocking placeholders. The provenance-gate 412 returns `detail` as an object; other
 * errors (400/404/500) return `detail` as a string.
 */
export function parseGenerationBlock(body: any, fallback = 'Generation failed'): GenerationBlock {
  const detail = body?.detail;
  const base = { kind: 'generic' as const, reattestRequired: false, invalidated: false };
  if (detail && typeof detail === 'object') {
    const message = typeof detail.message === 'string' ? detail.message : fallback;
    const gate = detail.attestation_gate && typeof detail.attestation_gate === 'object'
      ? detail.attestation_gate
      : null;
    if (gate || /not yet attested/i.test(message)) {
      const reviewStatus = typeof gate?.review_status === 'string' ? gate.review_status : undefined;
      const reportStatus = typeof gate?.report_status === 'string' ? gate.report_status : undefined;
      // Invalidated by an edit: review went stale, or the report fell back to
      // ready_for_attorney after having been finalized.
      const invalidated =
        (reviewStatus !== undefined && /stale/i.test(reviewStatus)) ||
        (reportStatus === 'ready_for_attorney' && gate?.finalized === true);
      return {
        message,
        blockingPlaceholders: [],
        kind: 'not_attested',
        reattestRequired: true,
        invalidated,
        electedMethod: typeof gate?.elected_method === 'string' ? gate.elected_method : undefined,
        reviewStatus,
      };
    }
    return {
      ...base,
      message,
      blockingPlaceholders: Array.isArray(detail.blocking_placeholders)
        ? detail.blocking_placeholders
        : [],
    };
  }
  return {
    ...base,
    message: typeof detail === 'string' ? detail : fallback,
    blockingPlaceholders: [],
  };
}

/** True when any blocking placeholder is an attorney-owned letterhead field. */
export function hasLetterheadGap(placeholders: string[]): boolean {
  return placeholders.some((p) => LETTERHEAD_PLACEHOLDERS.includes(String(p).toUpperCase()));
}

/** The letterhead-specific missing fields, as human-readable words ("bar number"). */
export function letterheadGapFields(placeholders: string[]): string[] {
  return placeholders
    .filter((p) => LETTERHEAD_PLACEHOLDERS.includes(String(p).toUpperCase()))
    .map((p) => String(p).replace(/[[\]]/g, '').toLowerCase());
}

/**
 * Error text for a failed export/generate response. Attestation 412s get the
 * "blocked — not attested / re-attest required" wording; any other failure keeps
 * the parsed server message (never "[object Object]") or the supplied fallback.
 */
export async function responseErrorMessage(response: Response, fallback: string): Promise<string> {
  const body = await response.json().catch(() => ({}));
  const block = parseGenerationBlock(body, fallback);
  return block.kind === 'not_attested' ? attestationBlockLine(block) : block.message;
}
